import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useAuthBootstrap from "../../../auth/useAuthBootstrap";
import { probeLocalRecovery } from "./localRecoveryProbe";

// TRAIN execution identity (§9.8). AuthBootstrapProvider stays the only
// authentication authority; this is a TRAIN-scoped view of it:
//   { mode: "authenticated", userId, authStatus }  the bootstrap user;
//   { mode: "local", userId, authStatus }          the local owner hint, while
//                                                  bootstrap is not authenticated
//                                                  and the probe found data;
//   null                                           no TRAIN recovery surface.
// A local identity only selects which IndexedDB partition TRAIN reads. It never
// authorizes or parameterizes a server request: server-backed TRAIN actions
// require `canUseServer(identity)`. executionId is deliberately absent.

const LOCAL_AUTH_STATUSES = new Set(["resolving", "unavailable", "anonymous"]);
const NO_SESSION_LOSS = Object.freeze({ episode: 0, settled: true, result: null });
const LAUNCH_RECOVERY_STATUSES = new Set(["ACTIVE", "FINISHED"]);
const PROBE_PENDING = Object.freeze({ settled: false, result: null });
const PROBE_NONE = Object.freeze({ settled: true, result: null });

const TrainIdentityContext = createContext(undefined);

export function deriveTrainIdentity(bootstrap, probeResult, { probeConsumed = false } = {}) {
  const status = bootstrap?.status;
  if (status === "authenticated") {
    const userId = bootstrap.user?.id;
    return userId ? { mode: "authenticated", userId, authStatus: status } : null;
  }
  // The probe describes this page load's startup. Once bootstrap has been
  // authenticated it is spent: a later non-authenticated state never
  // resurrects it. A Session lost after that is `deriveSessionLossIdentity`.
  if (probeConsumed || !probeResult?.userId || !LOCAL_AUTH_STATUSES.has(status)) return null;
  return { mode: "local", userId: probeResult.userId, authStatus: status };
}

/**
 * Local identity after the Session of an authenticated TRAIN is lost (expired,
 * revoked, re-resolved anonymous): the TRAIN of that user stays mounted, from
 * local data only (§11.4, I-26). Never after an explicit logout.
 *
 * `owner` is the user the bootstrap was authenticated as. Until the read-only
 * re-probe of this loss answers, TRAIN keeps that user's partition, so nothing
 * unmounts; the owner is also the `meta.lastAuthenticatedUserId` that
 * bootstrap just recorded. Once the re-probe has answered, its result decides
 * exactly as the startup probe does: no recoverable data means no local shell.
 */
export function deriveSessionLossIdentity(bootstrap, { owner, reprobe = null } = {}) {
  const status = bootstrap?.status;
  if (!owner || bootstrap?.loggedOut || !LOCAL_AUTH_STATUSES.has(status)) return null;
  const userId = reprobe?.settled ? reprobe.result?.userId : owner;
  return userId ? { mode: "local", userId, authStatus: status } : null;
}

// Server-dependent Workout actions run only for an authenticated identity (§9.8).
export function canUseServer(identity) {
  return identity?.mode === "authenticated";
}

// "Sign in to sync" (§11.4): local Workout data of the recoverable user while
// the global auth state is known to be anonymous / session-invalid. Derived
// from the identity, not from a record having reached AUTH_REQUIRED.
export function needsSignInToSync(identity) {
  return identity?.mode === "local" && identity.authStatus === "anonymous";
}

// Without the provider (a TRAIN screen rendered in isolation) TRAIN keeps its
// pre-Phase 8 contract: the authenticated bootstrap user, never local recovery.
function bootstrapOnlyIdentity(bootstrap) {
  const userId = bootstrap?.user?.id;
  if (!userId || (bootstrap.status && bootstrap.status !== "authenticated")) return null;
  return { mode: "authenticated", userId, authStatus: "authenticated" };
}

function currentPathname() {
  return typeof window === "undefined" ? null : window.location.pathname;
}

export function TrainIdentityProvider({ children, probe = null }) {
  const bootstrap = useAuthBootstrap();
  const runProbe = probe || probeLocalRecovery;
  // Without IndexedDB the default probe can only answer "none", so it is
  // settled from the first render and nothing waits on it.
  const [probeState, setProbeState] = useState(() => (
    probe || typeof indexedDB !== "undefined" ? PROBE_PENDING : PROBE_NONE
  ));
  const consumedRef = useRef(false);
  if (bootstrap.status === "authenticated") consumedRef.current = true;
  const probeConsumed = consumedRef.current;
  // Session loss (H8-2): the user this page load was last authenticated as,
  // and a counter of losses so each one is re-probed afresh. An explicit
  // logout forgets the owner: it never becomes automatic local recovery.
  const sessionOwnerRef = useRef(null);
  const lossCountRef = useRef(0);
  const inLossRef = useRef(false);
  if (bootstrap.status === "authenticated") {
    sessionOwnerRef.current = bootstrap.user?.id || null;
    inLossRef.current = false;
  } else if (bootstrap.loggedOut) {
    sessionOwnerRef.current = null;
    inLossRef.current = false;
  } else if (sessionOwnerRef.current && !inLossRef.current) {
    inLossRef.current = true;
    lossCountRef.current += 1;
  }
  const lossEpisode = inLossRef.current ? lossCountRef.current : 0;
  const [reprobeState, setReprobeState] = useState(NO_SESSION_LOSS);
  // Launch recovery (§9.8): only a page load that started at `/`, only once.
  const launchPathRef = useRef(currentPathname());
  const launchRedirectRef = useRef(false);

  // Started once, at mount, in parallel with the bootstrap request. A repeated
  // effect (StrictMode) reuses the same read instead of probing again.
  const runProbeRef = useRef(runProbe);
  const needsProbeRef = useRef(!probeState.settled);
  const probeRunRef = useRef(null);
  useEffect(() => {
    if (!needsProbeRef.current) return undefined;
    if (!probeRunRef.current) {
      probeRunRef.current = Promise.resolve()
        .then(() => runProbeRef.current())
        .catch(() => null);
    }
    let active = true;
    probeRunRef.current.then((result) => {
      if (active) setProbeState({ settled: true, result: result || null });
    });
    return () => {
      active = false;
    };
  }, []);

  // Each Session loss re-reads the owner hint and that user's partition with
  // the same read-only, bounded probe as startup (never a write).
  useEffect(() => {
    if (!lossEpisode) return undefined;
    let active = true;
    Promise.resolve()
      .then(() => runProbeRef.current())
      .catch(() => null)
      .then((result) => {
        if (active) setReprobeState({ episode: lossEpisode, settled: true, result: result || null });
      });
    return () => {
      active = false;
    };
  }, [lossEpisode]);
  const reprobe = lossEpisode && reprobeState.episode === lossEpisode ? reprobeState : null;

  const derived = deriveTrainIdentity(bootstrap, probeState.result, { probeConsumed }) ||
    (lossEpisode
      ? deriveSessionLossIdentity(bootstrap, { owner: sessionOwnerRef.current, reprobe })
      : null);
  const mode = derived?.mode || null;
  const userId = derived?.userId || null;
  const authStatus = derived?.authStatus || null;
  // Stable object identity while its fields are unchanged.
  const identity = useMemo(
    () => (mode ? { mode, userId, authStatus } : null),
    [authStatus, mode, userId]
  );

  const consumeLaunchRedirect = useCallback(() => {
    if (launchRedirectRef.current) return false;
    launchRedirectRef.current = true;
    return true;
  }, []);

  // Reconnect recovery (H8-4, §9.8): while a local TRAIN waits on an
  // unreachable backend, the browser coming back online or the page becoming
  // visible re-resolves the Session once (deduplicated by the provider). No
  // timer, no retry while offline; a failure leaves the local shell unchanged.
  const reresolveSession = bootstrap.reresolveSession;
  const reconnectPending = identity?.mode === "local" && bootstrap.status === "unavailable";
  useEffect(() => {
    if (
      !reconnectPending ||
      typeof reresolveSession !== "function" ||
      typeof window === "undefined"
    ) {
      return undefined;
    }
    const resolveIfOnline = () => {
      if (window.navigator?.onLine === false) return;
      void reresolveSession();
    };
    const resolveIfVisible = () => {
      if (document.visibilityState === "visible") resolveIfOnline();
    };
    window.addEventListener("online", resolveIfOnline);
    document.addEventListener("visibilitychange", resolveIfVisible);
    return () => {
      window.removeEventListener("online", resolveIfOnline);
      document.removeEventListener("visibilitychange", resolveIfVisible);
    };
  }, [reconnectPending, reresolveSession]);

  const recoveryResult = lossEpisode ? reprobe?.result || null : probeState.result;
  const localRecovery = identity?.mode === "local" ? recoveryResult : null;
  const value = useMemo(() => ({
    identity,
    // Still waiting for a probe answer that could change `/train/*` routing.
    probePending: !probeState.settled && !probeConsumed,
    // An ACTIVE/FINISHED record of the local identity: the launch redirect and
    // the "Return to workout" way back. A pending SAVED record alone is not one.
    hasOpenLocalWorkout: Boolean(localRecovery && LAUNCH_RECOVERY_STATUSES.has(localRecovery.status)),
    launchedAtRoot: launchPathRef.current === "/",
    consumeLaunchRedirect,
  }), [consumeLaunchRedirect, localRecovery, probeConsumed, probeState.settled, identity]);

  return createElement(TrainIdentityContext.Provider, { value }, children);
}

export function useTrainRecovery() {
  return useContext(TrainIdentityContext) || null;
}

export default function useTrainIdentity() {
  const context = useContext(TrainIdentityContext);
  const bootstrap = useAuthBootstrap();
  return context ? context.identity : bootstrapOnlyIdentity(bootstrap);
}
